import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Checkbox, IconButton, MenuItem, Select, Snackbar, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import { CardWrapper, ButtonOutlined } from '../../UI';
import * as C from '../../../theme/colors.constants';
import { contarSelecionados, estaTudoSelecionado } from '../Documentos/documentosProcessoController';
import { buscarArquivoDocumentoProcessoPorIdDocumentoService } from '../../../../services/core/processos/processoDocumentoService';
import { downloadFile, openPagePDF } from '../../../../helpers/utils/utils';
import DialogSalvarDocumentoProcesso from '../Documentos/DialogSalvarDocumentoProcesso';
import DialogExcluirDocumentoProcesso from '../Documentos/DialogExcluirDocumentoProcesso';
import DialogEnviarEmailDocumentoProcesso from '../Documentos/DialogEnviarEmailDocumentoProcesso';

const NAO_EXISTE_DOCUMENTO = 'Não existem documentos anexados ao Processo';
const ESTADO_INICIAL_DIALOG_DOCUMENTO = { aberto: false, documento: null, isEditar: false };

/**
 * Aba Documentos (Processo) — print `grid_check_docs.png`: barra com "ENVIAR"
 * e "+ NOVO" acima do card, e a tabela com paginação "Linhas por página" no
 * rodapé.
 *
 * AW2-1094 Etapa 1: grid real — `documentos`/`documentosTotal`/
 * `documentosPagina`/`documentosTamanhoPagina` (já buscados pelo container na
 * Etapa 0, via `documentosProcessoController.mapearDocumentos`) substituem o
 * mock estático `DOCUMENTOS`. Paginação mantém o MESMO visual (setas
 * prev/next + "Linhas por página", não `TablePagination` do MUI) — só a
 * fonte do estado muda de local para as props do container (paginação real
 * no servidor). Coluna "Responsável" adicionada (confirmada existente na
 * tela real, ausente do AC2 do Jira) e coluna "Ações" reservada vazia
 * (populada nas Etapas 3-5).
 *
 * Seleção (Etapa 2): `allIdsDocumentos` (Map<id, boolean> de TODOS os ids do
 * processo, buscado pelo container na Etapa 0) substitui o antigo estado
 * local `marcados` — persiste ao trocar de página porque não depende de
 * `documentos` (só a página atual), e as funções puras imutáveis de
 * `documentosProcessoController.js` (`contarSelecionados`/
 * `estaTudoSelecionado`) derivam contagem/estado "tudo selecionado" direto
 * do Map. Checkbox de linha gated por `podeModificarFase` (mesmo padrão de
 * `TableBodyDocumentosProcesso.jsx` real).
 *
 * Baixar/Visualizar (Etapa 3): reaproveita `buscarArquivoDocumentoProcessoPorIdDocumentoService`
 * + `downloadFile`/`openPagePDF` (mesmo par já usado em `DialogDocumentosCliente.jsx`,
 * AW2-1136 Etapa 5) — nenhuma lógica de manipulação de blob reimplementada.
 * Sempre visíveis, NÃO gated por `podeModificarFase` (confirmado na tela real
 * — downloads não dependem de fase). "Visualizar" desabilitado quando
 * `extensao` não é PDF (`hasNotPdf`, mesmo guard de
 * `TableBodyDocumentosProcesso.jsx` real).
 *
 * "+ Novo"/Editar (Etapa 4): `DialogSalvarDocumentoProcesso.jsx` novo,
 * único estado consolidado (`dialogDocumento = { aberto, documento, isEditar }`,
 * mesmo padrão de `dialogTitulo` em `AbaAcompanhamento.jsx`) em vez de 2
 * booleanos soltos. Resetado ao trocar de `idProcesso` — mesma regra já
 * documentada no CLAUDE.md do módulo ("estado de UI... também precisa
 * resetar ao trocar idProcesso"), já que este componente sobrevive à troca
 * de processo enquanto a aba "documentos" continuar ativa. "+ Novo" e o
 * ícone "Editar" gated por `podeModificarFase`, mesmo padrão de
 * `TableBodyDocumentosProcesso.jsx`/`DocumentosProcesso.jsx` reais. Sucesso
 * do dialog chama `onDocumentoMutado` (já buscado pelo container na Etapa 0).
 *
 * Excluir (Etapa 5): `DialogExcluirDocumentoProcesso.jsx` novo — confirmação
 * simples (sem Motivo obrigatório, diferente de `DialogExcluirTitulo.jsx`;
 * a exclusão real de documento não tem esse campo). Estado próprio
 * `documentoParaExcluir` (não fundido a `dialogDocumento`, mesmo padrão de
 * `tituloParaExcluir` separado de `dialogTitulo` em `AbaAcompanhamento.jsx`),
 * também resetado ao trocar de `idProcesso`. Ícone "Excluir" gated por
 * `podeModificarFase`.
 *
 * Enviar e-mail em massa (Etapa 6): `DialogEnviarEmailDocumentoProcesso.jsx`
 * novo — botão "Enviar" do cabeçalho (já `disabled` desde a Etapa 2) abre o
 * dialog passando o `allIdsDocumentos` (Map) ATUAL diretamente, não uma
 * cópia — `idProcessoDocumento` do payload é sempre recalculado do Map no
 * momento do submit. Estado próprio `enviarEmailAberto` (boolean simples,
 * sem dado de linha associado, diferente de `dialogDocumento`/
 * `documentoParaExcluir`), também resetado ao trocar de `idProcesso`. Sem
 * `onSucesso`/refetch — enviar e-mail não muta nenhum documento (mesmo
 * comportamento da tela real, `EnviarEmailDocumentoProcesso.jsx`, que só
 * mostra um toast e fecha, sem chamar `onUpdateTable`).
 */
const EXT_IMAGEM = ['ico', 'png', 'jpg', 'jpeg', 'Imagem'];

export default function AbaDocumentos({
  documentos, documentosTotal, documentosPagina, documentosTamanhoPagina,
  onChangePaginaDocumentos, onChangeTamanhoPaginaDocumentos,
  allIdsDocumentos, podeModificarFase, onSelecionarDocumento, onSelecionarTodosDocumentos,
  idProcesso, onDocumentoMutado,
}) {
  const visiveis = documentos || [];
  const inicio = documentosPagina * documentosTamanhoPagina;
  const ultimaPagina = documentosTamanhoPagina > 0
    ? Math.ceil(documentosTotal / documentosTamanhoPagina) - 1
    : 0;

  const totalSelecionados = contarSelecionados(allIdsDocumentos);
  const tudoSelecionado = estaTudoSelecionado(allIdsDocumentos);

  const [erro, setErro] = useState('');
  const onFecharErro = () => setErro('');

  const onClickBaixar = documento => async () => {
    try {
      const { data } = await buscarArquivoDocumentoProcessoPorIdDocumentoService(documento.id);
      downloadFile(documento.nomeArquivo, data);
    } catch {
      setErro(NAO_EXISTE_DOCUMENTO);
    }
  };

  const onClickVisualizar = documento => async () => {
    try {
      const { data } = await buscarArquivoDocumentoProcessoPorIdDocumentoService(documento.id);
      openPagePDF(data);
    } catch {
      setErro(NAO_EXISTE_DOCUMENTO);
    }
  };

  const [dialogDocumento, setDialogDocumento] = useState(ESTADO_INICIAL_DIALOG_DOCUMENTO);
  const [documentoParaExcluir, setDocumentoParaExcluir] = useState(null);
  const [enviarEmailAberto, setEnviarEmailAberto] = useState(false);

  useEffect(() => {
    setDialogDocumento(ESTADO_INICIAL_DIALOG_DOCUMENTO);
    setDocumentoParaExcluir(null);
    setEnviarEmailAberto(false);
  }, [idProcesso]);

  const onAbrirNovoDocumento = () => {
    setDialogDocumento({ aberto: true, documento: null, isEditar: false });
  };

  const onAbrirEditarDocumento = (documento) => {
    setDialogDocumento({ aberto: true, documento, isEditar: true });
  };

  const onFecharDialogDocumento = () => {
    setDialogDocumento(atual => ({ ...atual, aberto: false }));
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5,
      }}
      >
        <ButtonOutlined
          startIcon={<SendIcon />}
          disabled={totalSelecionados === 0}
          onClick={() => setEnviarEmailAberto(true)}
          sx={{ border: 'none', '&:hover': { border: 'none', backgroundColor: 'transparent' } }}
        >
          ENVIAR
          {totalSelecionados ? ` (${totalSelecionados})` : ''}
        </ButtonOutlined>
        <ButtonOutlined startIcon={<AddIcon />} disabled={!podeModificarFase} onClick={onAbrirNovoDocumento}>
          NOVO
        </ButtonOutlined>
      </Box>

      <CardWrapper>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    disabled={!podeModificarFase}
                    checked={tudoSelecionado}
                    indeterminate={totalSelecionados > 0 && !tudoSelecionado}
                    onChange={e => onSelecionarTodosDocumentos(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Extensão</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Nome do Arquivo</TableCell>
                <TableCell align="center">Original Arquivado</TableCell>
                <TableCell align="center">Oculto</TableCell>
                <TableCell align="center">Data de Arquivamento</TableCell>
                <TableCell>Responsável</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiveis.map((d, i) => (
                <TableRow key={d.id} hover sx={{ backgroundColor: i % 2 === 1 ? C.paleGrey : undefined }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      disabled={!podeModificarFase}
                      checked={Boolean(allIdsDocumentos.get(d.id))}
                      onChange={() => onSelecionarDocumento(d.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {EXT_IMAGEM.includes(d.extensao) ? (
                        <ImageOutlinedIcon sx={{ fontSize: 17, color: C.tangerine }} />
                      ) : (
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 17, color: C.navyLight }} />
                      )}
                      {d.extensao}
                    </Box>
                  </TableCell>
                  <TableCell>{d.tipo}</TableCell>
                  <TableCell>
                    <Tooltip title={d.descricao || ''} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.descricao}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={d.nomeArquivo} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.nomeArquivo}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {d.originalArquivada ? <CheckIcon sx={{ fontSize: 17, color: C.navy }} /> : null}
                  </TableCell>
                  <TableCell align="center">
                    {d.oculto ? <CheckIcon sx={{ fontSize: 17, color: C.navy }} /> : null}
                  </TableCell>
                  <TableCell align="center">{d.data}</TableCell>
                  <TableCell>
                    <Tooltip title={d.responsavel || ''} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.responsavel}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Baixar">
                      <IconButton size="small" onClick={onClickBaixar(d)} aria-label="Baixar documento">
                        <CloudDownloadIcon sx={{ fontSize: 18, color: C.navy }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={d.extensao?.toUpperCase() === 'PDF' ? 'Visualizar' : ''}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={d.extensao?.toUpperCase() !== 'PDF'}
                          onClick={onClickVisualizar(d)}
                          aria-label="Visualizar documento"
                        >
                          <SearchIcon sx={{ fontSize: 18, color: C.navy }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!podeModificarFase}
                          onClick={() => onAbrirEditarDocumento(d)}
                          aria-label="Editar documento"
                        >
                          <EditIcon sx={{ fontSize: 18, color: C.navy }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!podeModificarFase}
                          onClick={() => setDocumentoParaExcluir(d)}
                          aria-label="Excluir documento"
                        >
                          <DeleteIcon sx={{ fontSize: 18, color: C.navy }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {visiveis.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">Não existem documentos anexados ao Processo</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2,
        }}
        >
          <Typography variant="body2">Linhas por página:</Typography>
          <Select
            size="small"
            value={documentosTamanhoPagina}
            // `Select` do MUI chama `onChange(event, child)` — repassar só
            // `event` evita que o nó React do `MenuItem` selecionado caia no
            // parâmetro `extra` do hook (que usaria o valor errado se uma
            // etapa futura passar a consumi-lo; hoje `documentosHook` ignora
            // `extra`, então era inofensivo, mas o contrato ficava frágil).
            onChange={event => onChangeTamanhoPaginaDocumentos(event)}
            variant="standard"
            sx={{ minWidth: 56 }}
          >
            {[5, 10, 25].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
          <Typography variant="body2">
            {documentosTotal > 0 ? inicio + 1 : 0}
            –
            {Math.min(inicio + documentosTamanhoPagina, documentosTotal)}
            {' '}
            de
            {documentosTotal}
          </Typography>
          <IconButton
            size="small"
            disabled={documentosPagina === 0}
            onClick={() => onChangePaginaDocumentos(documentosPagina - 1)}
            sx={{ color: C.navy }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            disabled={documentosPagina >= ultimaPagina}
            onClick={() => onChangePaginaDocumentos(documentosPagina + 1)}
            sx={{ color: C.navy }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </CardWrapper>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={onFecharErro}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErro}>
          {erro}
        </Alert>
      </Snackbar>

      <DialogSalvarDocumentoProcesso
        open={dialogDocumento.aberto}
        documento={dialogDocumento.documento}
        isEditar={dialogDocumento.isEditar}
        idProcesso={idProcesso}
        onClose={onFecharDialogDocumento}
        onSucesso={onDocumentoMutado}
      />

      <DialogExcluirDocumentoProcesso
        open={Boolean(documentoParaExcluir)}
        documento={documentoParaExcluir}
        idProcesso={idProcesso}
        onClose={() => setDocumentoParaExcluir(null)}
        onSucesso={onDocumentoMutado}
      />

      <DialogEnviarEmailDocumentoProcesso
        open={enviarEmailAberto}
        idProcesso={idProcesso}
        allIdsDocumentos={allIdsDocumentos}
        onClose={() => setEnviarEmailAberto(false)}
      />
    </Box>
  );
}

AbaDocumentos.propTypes = {
  documentos: PropTypes.arrayOf(PropTypes.object),
  documentosTotal: PropTypes.number,
  documentosPagina: PropTypes.number,
  documentosTamanhoPagina: PropTypes.number,
  onChangePaginaDocumentos: PropTypes.func,
  onChangeTamanhoPaginaDocumentos: PropTypes.func,
  allIdsDocumentos: PropTypes.instanceOf(Map),
  podeModificarFase: PropTypes.bool,
  onSelecionarDocumento: PropTypes.func,
  onSelecionarTodosDocumentos: PropTypes.func,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDocumentoMutado: PropTypes.func,
};

AbaDocumentos.defaultProps = {
  documentos: [],
  documentosTotal: 0,
  documentosPagina: 0,
  documentosTamanhoPagina: 5,
  onChangePaginaDocumentos: () => null,
  onChangeTamanhoPaginaDocumentos: () => null,
  allIdsDocumentos: new Map(),
  podeModificarFase: false,
  onSelecionarDocumento: () => null,
  onSelecionarTodosDocumentos: () => null,
  idProcesso: null,
  onDocumentoMutado: () => null,
};
